import { FLAT_RATE_PLANS } from "@common/flatRateBilling";
import {
    getOrganizationAssignablePlans,
    OrganizationAssignablePlan,
    RESTRICTED_ORGANIZATION_CLIENT_PLANS,
} from "@common/constants/organization-billing";
import { FC, useCallback, useMemo, useState } from "react";
import classNames from "classnames";
import { Drawer } from "@design-system/Drawer/Drawer";
import { WorkspaceSchema } from "@common/schemas/workspace.schema";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import styles from "../BillingPage.module.scss";
import { useBillableClients } from "../../useBillableClients";
import planDetailsStyles from "./PlanDetails.module.scss";
import { PlanDetails } from "./PlanDetails";
import { ClientsSectionRow } from "./ClientsSectionRow";
import { PlanDetailsHeader } from "./PlanDetailsHeader";

export const ClientsSection: FC = () => {
    const [selectedPlan, setSelectedPlan] = useState<FLAT_RATE_PLANS | null>(
        null,
    );

    const allowedPlans = useActiveOrganization()?.allowedPlans;
    const billableOrganizationClients = useBillableClients();

    const clientsByPlan = useMemo(() => {
        const result: Record<FLAT_RATE_PLANS, WorkspaceSchema[]> = {
            [FLAT_RATE_PLANS.FREE]: [],
            [FLAT_RATE_PLANS.BASIC]: [],
            [FLAT_RATE_PLANS.PLUS]: [],
            [FLAT_RATE_PLANS.ADVANCED]: [],
            [FLAT_RATE_PLANS.ENTERPRISE]: [],
            [FLAT_RATE_PLANS.FRESHBOOKS]: [],
            [FLAT_RATE_PLANS.READ_ONLY]: [],
            [FLAT_RATE_PLANS.TAX]: [],
        };

        for (const client of billableOrganizationClients) {
            if (client.plan) {
                result[client.plan ?? FLAT_RATE_PLANS.FREE].push(client);
            }
        }

        return result;
    }, [billableOrganizationClients]);

    const billingTablePlans = useMemo((): OrganizationAssignablePlan[] => {
        const assignablePlans = getOrganizationAssignablePlans(allowedPlans);
        const missingRestrictedPlans =
            RESTRICTED_ORGANIZATION_CLIENT_PLANS.filter(
                (restrictedPlan) =>
                    clientsByPlan[restrictedPlan].length > 0 &&
                    !assignablePlans.some((plan) => plan === restrictedPlan),
            );

        return [...assignablePlans, ...missingRestrictedPlans];
    }, [allowedPlans, clientsByPlan]);

    const openPlanDetails = useCallback(
        (plan: FLAT_RATE_PLANS) => {
            setSelectedPlan(clientsByPlan[plan]?.length > 0 ? plan : null);
        },
        [clientsByPlan],
    );

    return (
        <>
            <article className={styles.section}>
                <header className={styles.header}>
                    <h2 className={styles.heading}>Clients</h2>
                </header>

                <main className={styles.sectionContent}>
                    <div className={styles.grid}>
                        {billingTablePlans.map((plan) => (
                            <ClientsSectionRow
                                key={plan}
                                plan={plan}
                                clientsCount={clientsByPlan[plan].length}
                                onPlanSelected={openPlanDetails}
                            />
                        ))}

                        <footer
                            className={classNames(styles.row, styles.footer)}
                        >
                            <div
                                className={classNames(
                                    styles.column,
                                    styles.item,
                                )}
                            >
                                Total
                            </div>
                            <div
                                className={classNames(
                                    styles.column,
                                    styles.value,
                                )}
                            >
                                {billableOrganizationClients.length}
                            </div>
                        </footer>
                    </div>
                </main>
            </article>

            <Drawer
                contentClassName={planDetailsStyles.drawer}
                open={!!selectedPlan}
                onClose={() => setSelectedPlan(null)}
                headerAddonLeft={<PlanDetailsHeader />}
                direction="right"
                modal={false}
                noPadding
            >
                {selectedPlan && <PlanDetails plan={selectedPlan} />}
            </Drawer>
        </>
    );
};
